import { useState, useRef, FormEvent, ChangeEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useCursorContext } from '../../hooks/useCursorContext';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

const subjectOptions = [
  'Projektanfrage',
  'Freelance Anfrage',
  'Zusammenarbeit',
  'Sonstiges',
];

// Replace with your Formspree form ID
const FORMSPREE_ID = 'YOUR_FORMSPREE_ID';

export function ContactForm() {
  const { setCursorState } = useCursorContext();
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const validateField = (name: keyof FormData, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name ist erforderlich';
        if (value.trim().length < 2) return 'Name ist zu kurz';
        break;
      case 'email':
        if (!value.trim()) return 'E-Mail ist erforderlich';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Ungültige E-Mail-Adresse';
        break;
      case 'subject':
        if (!value) return 'Bitte wählen Sie ein Thema';
        break;
      case 'message':
        if (!value.trim()) return 'Nachricht ist erforderlich';
        if (value.trim().length < 10) return 'Nachricht ist zu kurz';
        break;
    }
    return undefined;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    (Object.keys(formData) as Array<keyof FormData>).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleBlur = (name: keyof FormData) => {
    setFocusedField(null);
    const error = validateField(name, formData[name]);
    if (error) {
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setStatus('submitting');

    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const inputClasses = (field: keyof FormData) =>
    `w-full px-0 py-4 bg-transparent border-b text-white placeholder-white/30 focus:outline-none transition-colors ${
      errors[field]
        ? 'border-red-500'
        : focusedField === field
          ? 'border-emerald-500'
          : 'border-white/20'
    }`;

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
      {/* Name */}
      <div className="relative">
        <label
          htmlFor="name"
          className={`absolute left-0 transition-all duration-300 ${
            focusedField === 'name' || formData.name
              ? 'text-xs text-emerald-500 -top-2'
              : 'text-white/30 top-4'
          }`}
        >
          Name
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          onFocus={() => setFocusedField('name')}
          onBlur={() => handleBlur('name')}
          onMouseEnter={() => setCursorState('hovering')}
          onMouseLeave={() => setCursorState('default')}
          className={inputClasses('name')}
          disabled={status === 'submitting'}
        />
        <AnimatePresence>
          {errors.name && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="mt-2 text-xs text-red-500"
            >
              {errors.name}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Email */}
      <div className="relative">
        <label
          htmlFor="email"
          className={`absolute left-0 transition-all duration-300 ${
            focusedField === 'email' || formData.email
              ? 'text-xs text-emerald-500 -top-2'
              : 'text-white/30 top-4'
          }`}
        >
          E-Mail
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          onFocus={() => setFocusedField('email')}
          onBlur={() => handleBlur('email')}
          onMouseEnter={() => setCursorState('hovering')}
          onMouseLeave={() => setCursorState('default')}
          className={inputClasses('email')}
          disabled={status === 'submitting'}
        />
        <AnimatePresence>
          {errors.email && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="mt-2 text-xs text-red-500"
            >
              {errors.email}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Subject */}
      <div className="relative">
        <label
          htmlFor="subject"
          className={`absolute left-0 transition-all duration-300 ${
            focusedField === 'subject' || formData.subject
              ? 'text-xs text-emerald-500 -top-2'
              : 'text-white/30 top-4'
          }`}
        >
          Betreff
        </label>
        <select
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          onFocus={() => setFocusedField('subject')}
          onBlur={() => handleBlur('subject')}
          onMouseEnter={() => setCursorState('hovering')}
          onMouseLeave={() => setCursorState('default')}
          className={`${inputClasses('subject')} appearance-none cursor-pointer`}
          disabled={status === 'submitting'}
        >
          <option value="" disabled className="bg-[#0a0a0a]">
            Bitte wählen
          </option>
          {subjectOptions.map((option) => (
            <option key={option} value={option} className="bg-[#0a0a0a]">
              {option}
            </option>
          ))}
        </select>
        <AnimatePresence>
          {errors.subject && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="mt-2 text-xs text-red-500"
            >
              {errors.subject}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Message */}
      <div className="relative">
        <label
          htmlFor="message"
          className={`absolute left-0 transition-all duration-300 ${
            focusedField === 'message' || formData.message
              ? 'text-xs text-emerald-500 -top-2'
              : 'text-white/30 top-4'
          }`}
        >
          Nachricht
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          onFocus={() => setFocusedField('message')}
          onBlur={() => handleBlur('message')}
          onMouseEnter={() => setCursorState('hovering')}
          onMouseLeave={() => setCursorState('default')}
          rows={4}
          className={`${inputClasses('message')} resize-none`}
          disabled={status === 'submitting'}
        />
        <AnimatePresence>
          {errors.message && (
            <motion.p
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              className="mt-2 text-xs text-red-500"
            >
              {errors.message}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Submit button */}
      <motion.button
        type="submit"
        disabled={status === 'submitting' || status === 'success'}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onMouseEnter={() => setCursorState('hovering')}
        onMouseLeave={() => setCursorState('default')}
        className={`w-full py-5 px-8 rounded-full font-medium tracking-wider uppercase text-sm flex items-center justify-center gap-3 transition-colors ${
          status === 'success'
            ? 'bg-emerald-500 text-black'
            : status === 'error'
              ? 'bg-red-500/20 text-red-500 border border-red-500/50'
              : 'bg-emerald-500 text-black hover:bg-emerald-400'
        }`}
      >
        {status === 'submitting' ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Wird gesendet...
          </>
        ) : status === 'success' ? (
          <>
            <Check className="w-5 h-5" />
            Nachricht gesendet!
          </>
        ) : status === 'error' ? (
          <>
            <AlertCircle className="w-5 h-5" />
            Fehler - Bitte erneut versuchen
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Nachricht senden
          </>
        )}
      </motion.button>
    </form>
  );
}
